import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid,
    InputAdornment, Stack, TextField, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { createTerminalModel, getTerminalModels } from '../services/terminalModelService';
import PageHeader from '../components/ui/PageHeader';
import MetricCard from '../components/ui/MetricCard';
import SmartPayIcon from '../components/ui/SmartPayIcon';

const emptyModel = {
    manufacturer: '',
    model: '',
    version: '',
    description: '',
    capabilities: {
        chip: true,
        contactless: true,
        mag_stripe: false,
        pin_pad: true,
        printer: true,
        manual_entry: false
    },
    additional_capabilities: {}
};

const capabilityLabels = {
    chip: 'Chip',
    contactless: 'Contactless',
    mag_stripe: 'Banda magnética',
    pin_pad: 'PIN pad',
    printer: 'Impresora',
    manual_entry: 'Captura manual'
};

const TerminalModels = () => {
    const [models, setModels] = useState([]);
    const [form, setForm] = useState(emptyModel);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getTerminalModels();
            setModels(response.items || []);
        } catch (error) {
            setMessage({ severity: 'error', text: 'No fue posible cargar los modelos de terminal.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true); setMessage(null);
        try {
            await createTerminalModel(form);
            setForm({ ...emptyModel, capabilities: { ...emptyModel.capabilities } });
            setDialogOpen(false);
            setMessage({ severity: 'success', text: 'Modelo agregado correctamente.' });
            await load();
        } catch (error) {
            setMessage({ severity: 'error', text: error.response?.data || 'No fue posible agregar el modelo.' });
        } finally {
            setSaving(false);
        }
    };

    const filteredModels = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return models;
        return models.filter((item) => `${item.manufacturer} ${item.model} ${item.version} ${item.description || ''}`.toLowerCase().includes(term));
    }, [models, search]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

    return (
        <Stack spacing={3.5}>
            <PageHeader eyebrow="Catálogo de hardware" title="Modelos de terminal" description="Define las capacidades físicas una sola vez y relaciónalas con cada número de serie." actions={
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Nuevo modelo</Button>
            } />
            {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Modelos registrados" value={models.length} helper="Versiones de hardware" icon={<SmartPayIcon name="models" size={62} />} /></Grid>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Con impresora" value={models.filter((item) => item.capabilities?.printer).length} helper="Capacidad de recibos" icon={<SmartPayIcon name="printer" size={62} />} /></Grid>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Contactless" value={models.filter((item) => item.capabilities?.contactless).length} helper="Lectura sin contacto" icon={<SmartPayIcon name="contactless" size={62} />} /></Grid>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Por revisar" value={models.filter((item) => item.additional_capabilities?.migration_status === 'REVIEW_REQUIRED').length} helper="Modelos migrados" icon={<SmartPayIcon name="review" size={62} />} /></Grid>
            </Grid>

            <Box>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Typography variant="h6">Modelos registrados ({filteredModels.length})</Typography>
                    <TextField size="small" placeholder="Buscar fabricante, modelo o versión" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: { md: 340 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon color="action" /></InputAdornment> }} />
                </Stack>
                <Grid container spacing={2}>
                    {filteredModels.map((item) => (
                        <Grid item xs={12} md={6} lg={4} key={item.id}>
                            <Card sx={{ height: '100%', transition: 'transform .18s ease, box-shadow .18s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 18px 46px rgba(16,45,71,.12)' } }}><CardContent sx={{ p: 2.7 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography variant="h6">{item.manufacturer} {item.model}</Typography>
                                        <Typography color="text.secondary">Versión {item.version}</Typography>
                                    </Box>
                                    <SmartPayIcon name="models" size={48} />
                                </Stack>
                                <Typography variant="body2" sx={{ my: 2, minHeight: 40 }}>{item.description || 'Sin descripción'}</Typography>
                                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                                    {Object.entries(item.capabilities || {}).map(([key, enabled]) => enabled && (
                                        <Chip key={key} size="small" label={capabilityLabels[key] || key} color={key === 'printer' ? 'primary' : 'default'} />
                                    ))}
                                </Stack>
                                {item.additional_capabilities?.migration_status === 'REVIEW_REQUIRED' && <Alert severity="warning" sx={{ mt: 2 }}>Modelo migrado: revisa sus capacidades.</Alert>}
                            </CardContent></Card>
                        </Grid>
                    ))}
                    {filteredModels.length === 0 && <Grid item xs={12}><Alert severity="info">No hay modelos que coincidan con la búsqueda.</Alert></Grid>}
                </Grid>
            </Box>

            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ pb: 1 }}>Nuevo modelo de terminal</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>Registra una versión física y selecciona únicamente las capacidades disponibles en ese hardware.</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}><TextField required fullWidth label="Fabricante" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></Grid>
                        <Grid item xs={12} md={4}><TextField required fullWidth label="Modelo" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></Grid>
                        <Grid item xs={12} md={4}><TextField required fullWidth label="Versión" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></Grid>
                        <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Capacidades</Typography>
                            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'action.hover' }}>
                                <Grid container>
                                    {Object.entries(form.capabilities).map(([key, checked]) => (
                                        <Grid item xs={12} sm={6} md={4} key={key}><FormControlLabel control={<Checkbox checked={checked} onChange={(e) => setForm((current) => ({ ...current, capabilities: { ...current.capabilities, [key]: e.target.checked } }))} />} label={capabilityLabels[key]} /></Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
                    <Button variant="contained" disabled={saving || !form.manufacturer.trim() || !form.model.trim() || !form.version.trim()} onClick={save}>{saving ? 'Guardando…' : 'Crear modelo'}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default TerminalModels;
